import { DNode } from '@dojo/framework/core/interfaces';
import { icache } from '@dojo/framework/core/middleware/icache';
import { createResourceMiddleware, createResourceTemplate } from '@dojo/framework/core/middleware/resources';
import { theme } from '@dojo/framework/core/middleware/theme';
import { create, v, w } from '@dojo/framework/core/vdom';
import Chip from '@dojo/widgets/chip';
import Tree, { TreeNodeOption } from '@dojo/widgets/tree';
import { isEmpty } from 'lodash-es';
import { publish } from 'pub-sub';
import { Keys } from '@holmescorp/common';
import { ParentTree, TrackedState } from '@holmescorp/lcc-interfaces';
import { createNlsMiddleware, keymap, pathmap, quizmap, userstate } from '@holmescorp/lcc-state';
import { numeral } from '@holmescorp/numeral';

import { css } from '../css/toc.m.css';

export type TocTreeNodeOptionType = 'root' | 'parent' | 'leaf' | 'link';
export type TocNodeQuizType = 'quiz' | 'flashcards';
export interface TocNodeLink {
	id: string;
	type: TocNodeQuizType;
	value: string;
	score?: string;
}

export interface TocTreeNodeOption extends TreeNodeOption {
	/**
	 * id, value, parent, hasChildren
	 */
	path?: string | null;
	completed?: boolean;
	data?: TrackedState;
	quiz?: TocNodeLink | undefined;
	flashcards?: TocNodeLink | undefined;
	nodeType?: TocTreeNodeOptionType;
}

export interface TableOfContentsProperties {
	onClose?(): void;
}

const resource = createResourceMiddleware();
const factory = create({
	icache,
	nls: createNlsMiddleware('ditabook'),
	theme,
	userstate,
	resource
}).properties<TableOfContentsProperties>();

export const TableOfContents = factory(({ id, properties, middleware: { nls, theme, userstate, resource } }) => {
	const {
		isPlaceholder,
		messages: {
			quiz: quizText = '[ditabook] quiz',
			flashcards: flashcardText = '[ditabook] flashcards',
			topic_root_order = '[]'
		}
	} = nls();

	if (isPlaceholder) {
		return null;
	}

	const { onClose } = properties();
	const themeCss = theme.classes(css);
	const overrides = {
		'@dojo/widgets/tree': {
			root: [themeCss.treeRoot],
			node: [themeCss.treeNode],
			nodeParent: [themeCss.indented],
			contentWrapper: [themeCss.contentWrapper],
			content: [themeCss.content],
			leaf: [themeCss.leaf],
			title: [themeCss.title],
			expander: [themeCss.expander]
		}
	};

	const lang = window.hcstatic.portal_language;
	const collator = new Intl.Collator(lang, { numeric: true, sensitivity: 'base' });
	const topicRootOrder = JSON.parse(topic_root_order);
	const getRoots = (status: ParentTree): TocTreeNodeOption[] => {
		return Object.keys(status)
			.filter((key: string): boolean => status[key].parent === null)
			.sort((a, b) => {
				if (topicRootOrder.length > 0) {
					return topicRootOrder.indexOf(a) - topicRootOrder.indexOf(b);
				} else {
					return collator.compare(a, b);
				}
			})
			.map((id: string): TocTreeNodeOption => {
				const { title: value } = keymap.get(id)!;
				const { data } = status[id];
				return { id, value, parent: 'root', hasChildren: true, data, nodeType: 'root' };
			});
	};

	const getTopicsets = (parent: string, status: ParentTree): TocTreeNodeOption[] => {
		return Object.keys(status)
			.filter((key): boolean => status[key].parent === parent)
			.sort(collator.compare)
			.map((id: string): TocTreeNodeOption => {
				const { title: value } = keymap.get(id)!;
				const { quiz: quizId, flashcards: fcId } = quizmap.get(id) || {};
				const { data } = status[id];
				const quiz: TocNodeLink | undefined = quizId
					? {
							id: quizId,
							type: 'quiz',
							value: quizText,
							score: `${numeral(data.percent).format('0%')}` || ''
					  }
					: undefined;
				const flashcards: TocNodeLink | undefined = fcId
					? {
							id: fcId,
							type: 'flashcards',
							value: flashcardText
					  }
					: undefined;
				return { id, value, parent, hasChildren: true, data, quiz, flashcards, nodeType: 'parent' };
			});
	};

	const getTopicgroups = (parent: string, status: ParentTree): TocTreeNodeOption[] => {
		return Object.keys(status)
			.filter((key: string): boolean => status[key].parent === parent)
			.sort(collator.compare)
			.map((id: string): TocTreeNodeOption => {
				const { title: value, path } = keymap.get(id)!;
				const { data } = status[id];
				const completed = data.complete === data.total;
				return { id, value, parent, hasChildren: false, path, completed, data, nodeType: 'leaf' };
			});
	};

	const getLinks = (parents: TocTreeNodeOption[]): TocTreeNodeOption[] => {
		return parents.map((parent): TocTreeNodeOption => {
			const { id, quiz, flashcards } = parent;
			// const hasQuiz = Boolean(quiz) || Boolean(flashcards);
			return { id: `${id}-links`, value: '', parent: id, hasChildren: false, quiz, flashcards, nodeType: 'link' };
		});
	};

	const getNodes = (): TocTreeNodeOption[] => {
		const { get: uget, path: upath } = userstate;
		const status: ParentTree = uget(upath('status'));
		const nodes: TocTreeNodeOption[] = getRoots(status);
		const topicsets = nodes
			.map((root): TocTreeNodeOption[] => {
				const { id } = root;
				return getTopicsets(id, status);
			})
			.flat();
		nodes.push(...topicsets);
		const topicgroups = topicsets
			.map((topicset): TocTreeNodeOption[] => {
				const { id } = topicset;
				return getTopicgroups(id, status);
			})
			.flat();
		nodes.push(...topicgroups);
		nodes.push(...getLinks(topicsets));
		return nodes;
	};

	const itemRenderer = (node: TocTreeNodeOption): DNode | DNode[] => {
		const renderNode = (node: TocTreeNodeOption): DNode | DNode[] => {
			const { id, value, path, data, completed, quiz, flashcards, nodeType } = node;
			const time = data?.readtime;
			const links = [];
			if (!isEmpty(quiz)) {
				links.push(quiz);
			}
			if (!isEmpty(flashcards)) {
				links.push(flashcards);
			}
			//			const { item, complete, readtime, leafItem } = theme.classes(css);

			// Dont need, using TopicStatusIcon widget instead
			// const checkSquare = w(Icon, {
			// 	faname: 'check-square',
			// 	altText: '',
			// 	xqa: 'completed-icon',
			// 	aria: { label: '' }
			// });

			switch (nodeType) {
				case 'root':
					return v('h1', {}, [value]);
				case 'leaf':
					return v('div', { classes: [themeCss.item, themeCss.leafItem] }, [
						v(
							'a',
							{
								onclick: (event: MouseEvent): void => {
									event.stopPropagation();
									const { view } = pathmap.get(path!)!;
									view();
								},
								onkeydown: (event: KeyboardEvent): void => {
									const { view } = pathmap.get(path!)!;
									switch (event.key) {
										case Keys.Enter:
										case Keys.Space:
										case Keys.Spacebar:
											event.preventDefault();
											view();
									}
								}
							},
							[v('h3', {}, [value])]
						),
						completed
							? v('div', { classes: [themeCss.complete] }, [
									w('TopicStatusIcon', { key: id, useQuizScore: false })
							  ])
							: null,
						!completed && time ? v('span', { classes: [themeCss.readtime] }, [time]) : null
					]);
				case 'parent':
					return v('h2', {}, [value]);
				case 'link':
					return links.map((link): DNode => {
						const { value, type, score = '' } = link!;
						const { parent } = node;
						return v(
							'a',
							{
								onclick: (event: MouseEvent): void => {
									event && event.stopPropagation();
									const { path } = keymap.get(parent)!;
									const { view } = pathmap.get(`${path}/${type}`)!;
									view();
									onClose && onClose();
								},
								onkeydown: (event: KeyboardEvent): void => {
									const { path } = keymap.get(parent)!;
									const { view } = pathmap.get(`${path}/${type}`)!;
									switch (event.key) {
										case Keys.Enter:
										case Keys.Space:
										case Keys.Spacebar:
											event.preventDefault();
											view();
											onClose && onClose();
									}
								}
							},
							[
								value,
								score && parseInt(score) > 0
									? v('div', { classes: [themeCss.chip] }, [
											w(
												Chip,
												{ classes: { '@dojo/widgets/chip': { root: [themeCss.chipRoot] } } },
												[{ label: score }]
											)
									  ])
									: null
							]
						);
					});
				default:
					return null;
			}
		};

		return renderNode(node);
	};

	const nodes = getNodes();
	const template = createResourceTemplate<TocTreeNodeOption>('id');
	//	const { root, indented, leaf } = theme.classes(css);

	const overview = v('div', { classes: [themeCss.video] }, [
		v(
			'a',
			{
				onclick: (event: MouseEvent): void => {
					event && event.stopPropagation();
					publish('site/video/13');
					onClose && onClose();
				},
				onkeydown: (event: KeyboardEvent): void => {
					switch (event.key) {
						case Keys.Enter:
						case Keys.Space:
						case Keys.Spacebar:
							event.preventDefault();
							publish('site/video/13');
							onClose && onClose();
					}
				}
			},
			[v('h1', {}, ['Learning System Overview video'])]
		)
	]);

	return v('div', { id: `toc-tree-${id}`, xqa: 'table-of-contents', classes: [themeCss.root, theme.variant()] }, [
		overview,
		w(
			Tree,
			{
				classes: overrides,
				key: 'toc',
				resource: resource({ template: template({ id, data: nodes }) })
			},
			[itemRenderer]
		)
	]);
});

export default TableOfContents;
